import { cellToBoundary, cellsToMultiPolygon, isValidCell } from 'h3-js'
import React, { useMemo } from 'react'
import { type ViewState, Layer, Source } from 'react-map-gl'
import { PLOT_COLORS } from '#app/consts'
import { useStoreContext } from '#app/store'

export const PlotsSelected = ({
	viewState,
	claimPlotId,
}: {
	viewState: ViewState
	claimPlotId?: string | null
}) => {
	const basketIds = useStoreContext(s => s.selectedPlotIds)
	const selectedPlotIds = useMemo(
		() =>
			claimPlotId && isValidCell(claimPlotId)
				? [...new Set([...basketIds, claimPlotId])]
				: basketIds,
		[claimPlotId, basketIds],
	)
	const highlightedPlotIds = useStoreContext(s => s.highlightedPlotIds)

	const multiplePlotsPolygon = useMemo(
		() => cellsToMultiPolygon(selectedPlotIds, true),
		[selectedPlotIds],
	)

	const size = useMemo(() => {
		if (viewState.zoom > 15) return 'large'
		if (viewState.zoom > 13) return 'regular'
		return 'small'
	}, [viewState.zoom])

	const { selectedFill, selectedLine } = PLOT_COLORS

	return (
		<>
			<Source
				type="geojson"
				data={{
					type: 'FeatureCollection',
					features: [
						{
							type: 'Feature',
							geometry: {
								type: 'MultiPolygon',
								coordinates: multiplePlotsPolygon,
							},
							id: 'selected-plots',
							properties: {},
						},
					],
				}}
			>
				<Layer
					{...{
						id: `selected-plots-fill-layer`,
						type: 'fill',
						paint: {
							'fill-color': selectedFill,
							'fill-opacity': 0.2,
						},
					}}
				/>
				{size === 'large' && (
					<Layer
						{...{
							id: `selected-plots-line-layer`,
							type: 'line',
							paint: {
								'line-color': selectedLine,
								'line-width': 2.5,
								'line-opacity': 1,
								'line-dasharray': [2.5, 2.5],
							},
						}}
					/>
				)}
				{size === 'regular' && (
					<Layer
						{...{
							id: `selected-plots-line-layer`,
							type: 'line',
							paint: {
								'line-color': selectedLine,
								'line-width': 2,
								'line-opacity': 1,
								'line-dasharray': [2, 2],
							},
						}}
					/>
				)}
				{size === 'small' && (
					<Layer
						{...{
							id: `selected-plots-line-layer`,
							type: 'line',
							paint: {
								'line-color': selectedLine,
								'line-width': 1.5,
								'line-opacity': 1,
								'line-dasharray': [1, 1],
							},
						}}
					/>
				)}
			</Source>

			{selectedPlotIds.map(plotId => {
				const coordinates = cellToBoundary(plotId, true)
				const isHighlighted = highlightedPlotIds.includes(plotId)

				if (!isHighlighted) return null

				return (
					<React.Fragment key={`selected-highlighted-${plotId}`}>
						<Source
							type="geojson"
							data={{
								type: 'FeatureCollection',
								features: [
									{
										type: 'Feature',
										geometry: {
											type: 'Polygon',
											coordinates: [coordinates],
										},
										id: plotId,
										properties: {},
									},
								],
							}}
						>
							<Layer
								{...{
									id: `${plotId}-fill-layer`,
									type: 'fill',
									paint: {
										'fill-color': '#d97706', //amber 600
										'fill-opacity': 0.3,
									},
								}}
							/>
						</Source>
					</React.Fragment>
				)
			})}
		</>
	)
}
